import type React from 'react'
import type { ReactNode } from 'react'
import { Fragment, cloneElement, createElement, isValidElement } from 'react'
import { decodeHtmlEntities } from '@/lib/decodeHtmlEntities'

type SupportedTag = 'p' | 'b' | 'strong' | 'i' | 'ul' | 'li' | 'a'

/** Custom renderer map — keyed by tag name.  Receives the parsed children and a stable key. */
export type TagRenderers = Partial<Record<SupportedTag, (children: ReactNode[], key: string) => ReactNode>>

/**
 * Optional href rewriter (e.g. Walmart native-app deeplinks).
 * Invoked after href sanitization so scheme allow-listing still applies first.
 */
export type ResolveExternalHref = (
    href: string,
    options?: { openExternally?: boolean },
) => string

interface ParsedAnchorProps {
    href?: string
    target?: '_blank'
    rel?: 'noopener noreferrer'
}

interface ParsedElement {
    tag: SupportedTag
    children: ReactNode[]
    props?: ParsedAnchorProps
}

const SUPPORTED_TAGS: ReadonlySet<SupportedTag> = new Set<SupportedTag>(['p', 'b', 'strong', 'i', 'ul', 'li', 'a'])
const TAG_REGEX = /<\/?\s*(p|b|strong|i|ul|li|a)(?:\s+[^>]*)?>/gi

function sanitizeHref(href: string | undefined): string | undefined {
    if (!href) {
        return undefined
    }

    // Allow standard web/mail/phone and local links.
    if (/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(href)) {
        return href
    }

    return undefined
}

function extractAttribute(tag: string, attributeName: string): string | undefined {
    const attrRegex = new RegExp(`${attributeName}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i')
    const match = tag.match(attrRegex)
    return match?.[1] ?? match?.[2] ?? match?.[3]
}

function parseAnchorProps(tag: string, resolveExternalHref?: ResolveExternalHref): ParsedAnchorProps {
    const rawHref = extractAttribute(tag, 'href')
    // Attribute text may contain entities (`&amp;`); decode before sanitize/resolve.
    const sanitizedHref = sanitizeHref(rawHref ? decodeHtmlEntities(rawHref) : undefined)
    const targetValue = extractAttribute(tag, 'target')
    const target = targetValue === '_blank' ? '_blank' : undefined
    const href =
        sanitizedHref && resolveExternalHref
            ? resolveExternalHref(sanitizedHref, { openExternally: target === '_blank' })
            : sanitizedHref

    if (target === '_blank') {
        return { href, target, rel: 'noopener noreferrer' }
    }

    return { href }
}

function isClosingTag(tag: string): boolean {
    return /^<\s*\//.test(tag)
}

const TAG_STYLES: Partial<Record<SupportedTag, React.CSSProperties>> = {
    p: {
        marginTop: 0,
        marginBottom: '1em',
    },
    i: {
        fontStyle: 'normal',
        fontSize: 16,
        fontWeight: 'var(--weight-text, 450)' as React.CSSProperties['fontWeight'],
        lineHeight: '150%',
        color: 'var(--mantine-color-gray-6)',
    },
    a: {
        color: 'var(--mantine-color-blue-6)',
        fontWeight: 400,
    },
    ul: {
        marginTop: 0,
    },
}

/** Tags that should be rendered as a different HTML element (e.g. `<i>` → `<span>`). */
const TAG_REMAP: Partial<Record<SupportedTag, string>> = {
    i: 'span',
}

function toElement(node: ParsedElement, key: string, renderers?: TagRenderers): ReactNode {
    const customRenderer = renderers?.[node.tag]
    if (customRenderer) {
        const rendered = customRenderer(node.children, key)
        if (node.tag === 'ul' && isValidElement(rendered)) {
            return cloneElement(rendered, {
                'data-parse-html-tag': 'ul',
            })
        }
        return rendered
    }

    const style = TAG_STYLES[node.tag]
    const renderTag = TAG_REMAP[node.tag] ?? node.tag
    return createElement(renderTag, { key, ...node.props, ...(style && { style }) }, node.children)
}

function isMeaningfulNode(node: ReactNode): boolean {
    if (typeof node === 'string') {
        return node.trim().length > 0
    }
    return node != null
}

function isHtmlElement(node: ReactNode, tag: string): node is React.ReactElement {
    return (
        isValidElement(node) &&
        (node.type === tag || (node.props as { 'data-parse-html-tag'?: string })['data-parse-html-tag'] === tag)
    )
}

function applyListBottomSpacing(nodes: ReactNode[]): ReactNode[] {
    return nodes.map((node, index) => {
        if (!isHtmlElement(node, 'ul')) {
            return node
        }

        const hasContentAfter = nodes.slice(index + 1).some(isMeaningfulNode)
        const existingStyle = (node.props as { style?: React.CSSProperties }).style

        return cloneElement(node, {
            style: {
                ...existingStyle,
                marginBottom: hasContentAfter ? '1em' : 0,
            },
        })
    })
}

export function parseHtml(
    content: string | null | undefined,
    renderers?: TagRenderers,
    resolveExternalHref?: ResolveExternalHref,
): ReactNode {
    if (!content) {
        return null
    }

    const output: ReactNode[] = []
    const stack: ParsedElement[] = []
    let cursor = 0
    let keyCounter = 0
    let match: RegExpExecArray | null

    const pushNode = (node: ReactNode): void => {
        const parent = stack.at(-1)
        if (parent) {
            parent.children.push(node)
            return
        }
        output.push(node)
    }

    while ((match = TAG_REGEX.exec(content)) !== null) {
        const [fullTag, rawTagName] = match
        const tagName = rawTagName.toLowerCase() as SupportedTag

        if (!SUPPORTED_TAGS.has(tagName)) {
            continue
        }

        const textChunk = content.slice(cursor, match.index)
        if (textChunk) {
            pushNode(textChunk)
        }

        if (isClosingTag(fullTag)) {
            const node = stack.pop()
            if (node && node.tag === tagName) {
                pushNode(toElement(node, `node-${keyCounter++}`, renderers))
            } else {
                // Keep unmatched closing tags as plain text.
                if (node) {
                    stack.push(node)
                }
                pushNode(fullTag)
            }
        } else {
            const props = tagName === 'a' ? parseAnchorProps(fullTag, resolveExternalHref) : undefined
            stack.push({ tag: tagName, children: [], props })
        }

        cursor = TAG_REGEX.lastIndex
    }

    const trailingText = content.slice(cursor)
    if (trailingText) {
        pushNode(trailingText)
    }

    while (stack.length > 0) {
        const node = stack.pop()
        if (!node) {
            continue
        }
        pushNode(toElement(node, `node-${keyCounter++}`, renderers))
    }

    const parsedOutput = applyListBottomSpacing(output)

    if (parsedOutput.length === 1) {
        return parsedOutput[0]
    }

    return createElement(Fragment, null, ...parsedOutput)
}

export default parseHtml